import {Button, DataStatus, RestrictedStatus, showNotification} from 'platform/components';
import {
  Box,
  BoxProps,
  Center,
  Show,
  Space,
  Spinner,
  Stack,
  useDevice,
  VStack,
} from 'platform/foundation';
import {css} from 'styled-components';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';
// eslint-disable-next-line no-restricted-imports -- Intended
import {matchPath, useLocation, useMatch, generatePath} from 'react-router-dom';

import {always, find, isNil, map, propEq} from 'ramda';
import {isArray, isNotNil, isNotString} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {RowControlWrapper} from 'features/eag-grid';

import {useNavigationBreadCrumb} from '../../hooks/useNavigationBreadCrumb';
import {MobileMenu} from '../MobileMenu/MobileMenu';
import {NumberedMenu} from '../NumberedMenu/NumberedMenu';
import {NumberedMenuItem} from '../NumberedMenu/NumberedMenuItem';

export type RoutedContent = {route: string; element: ReactNode};

export type NavigationItem = {
  content?: ReactNode | RoutedContent[];
  href: string;
} & NumberedMenuItem;

/** The section menu state, for a collapsed header that offers the sections itself. */
export type PageSectionNavigation = {
  items: NumberedMenuItem[];
  activeItemId: string | undefined;
  onItemChange: (itemId: string) => void;
};

export interface PageProps extends RequiredTestIdProps {
  navigation?: NavigationItem[];
  header?: ReactNode; //TODO: Use new EntityHeaderProps when ready / https://carvago.atlassian.net/browse/T20-15024
  /** Replaces `header` and receives the section menu state. */
  renderHeader?: (sectionNavigation: PageSectionNavigation) => ReactNode;
  children?: ReactNode;
  activityPanel?: ReactNode;
  isFullHeight?: boolean;
  onNavigate?: (item: NavigationItem) => void;
  isLocked?: boolean;
  isLoading?: boolean;
  activeItemId?: string;
  onItemSelect?: (itemId: string) => void;
  /** Hides the left section menu; the header then offers the sections. */
  isNavigationCollapsed?: boolean;
}

export function Page(props: PageProps) {
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const [breadcrumbProps] = useNavigationBreadCrumb();
  const rowControlDetailMatch = useMatch(breadcrumbProps?.rowControlDetail ?? '');
  const device = useDevice();

  const selectedItem = isNil(props.activeItemId)
    ? props.navigation?.find(({href, content}) => {
        const routes = [href.split('?')[0]];
        if (isArray(content)) {
          routes.push(...map((item) => item.route, content));
        }
        return routes.some((route) => matchPath(route, pathname));
      })
    : find(propEq(props.activeItemId, 'id'), props.navigation ?? []);

  const activeItem = isNil(props.activeItemId)
    ? (selectedItem ?? props.navigation?.[0])
    : selectedItem;
  const itemElement = isArray(activeItem?.content)
    ? activeItem?.content.find((routedContent) => matchPath(routedContent.route, pathname))?.element
    : activeItem?.content;

  const height = props.isFullHeight ? '100%' : undefined;
  // When activityPanel is present, the Stack must be height:'auto' so its content can overflow
  // #dms-app-container and create real scroll travel — required for position:sticky on the sidebar.
  // This applies regardless of the REDESIGN flag; the flag only affects the sidebar component itself.
  const autoHeight = props.isFullHeight && props.activityPanel ? 'auto' : height;

  const onItemChange = (itemId: string) => {
    if (isNotNil(props.onItemSelect)) {
      props.onItemSelect(itemId);
      return;
    }

    const selectedItem = find(propEq(itemId, 'id'), props.navigation ?? []);
    if (isNil(selectedItem)) {
      return;
    }
    navigate(selectedItem.href);
    props.onNavigate?.(selectedItem);
  };

  const header = isNil(props.renderHeader)
    ? props.header
    : props.renderHeader({
        items: props.navigation ?? [],
        activeItemId: activeItem?.id,
        onItemChange,
      });

  const redirectToUrl = () => {
    const redirectToOverviewUrl = props.navigation?.[0].href;

    if (!redirectToOverviewUrl) {
      showNotification.error(i18n.t('page.detailRestrictedArea.noRedirectToOverviewUrl'));
      return;
    }

    navigate(redirectToOverviewUrl);
  };

  const handleRowControlChange = (
    newEntity: string,
    sourceSearchParams: string,
    additionalRouteParams?: Record<string, string>
  ) => {
    if (isNotString(breadcrumbProps?.rowControlDetail)) {
      throw Error('You need to specify rowControlDetail');
    }

    navigate(
      generatePath(breadcrumbProps?.rowControlDetail as string, {
        ...rowControlDetailMatch?.params,
        id: newEntity,
        ...additionalRouteParams,
      }) + `?${sourceSearchParams}`
    );
  };

  const disabledOverlay = (
    <Center width="100%">
      <VStack>
        <RestrictedStatus
          headline={i18n.t('page.detailRestrictedArea.headline')}
          subheadline={i18n.t('page.detailRestrictedArea.subheadline')}
        />
        <Space vertical={4} />
        <Button
          data-testid={suffixTestId('goToOverview', props)}
          title={i18n.t('page.detailRestrictedArea.goToOverview')}
          onClick={redirectToUrl}
        />
        <Space vertical={4} />
        <Center>
          <RowControlWrapper
            onChange={handleRowControlChange}
            fetchStrategy={breadcrumbProps?.rowControlFetchStrategy}
          />
        </Center>
      </VStack>
    </Center>
  );

  const loadingOverlay = (
    <Center width="100%">
      <DataStatus isLoading />
    </Center>
  );

  const isActiveItemDisabled = activeItem && 'isDisabled' in activeItem && activeItem?.isDisabled;

  const context = match([isActiveItemDisabled, props.isLoading])
    .with([true, true], always(loadingOverlay))
    .with([true, false], always(disabledOverlay))
    .otherwise(always(props.children ?? itemElement));

  const isMobileOrTablet = device === 'mobile' || device === 'tablet';

  const boxProps = match(isMobileOrTablet)
    .returnType<BoxProps>()
    .with(true, () => ({
      padding: 3,
      height: '100%',
      width: '100%',
    }))
    .otherwise(() => ({
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 4,
      paddingRight: 0,
      width: 54,
      minWidth: 54,
    }));

  return (
    <VStack height={height}>
      {header}

      <Stack direction={isMobileOrTablet ? 'column' : 'row'} height={autoHeight}>
        <Show when={!isNil(props.navigation) && props.isNavigationCollapsed !== true}>
          <Box {...boxProps}>
            {match(isMobileOrTablet)
              .with(true, () => (
                <MobileMenu
                  items={props.navigation ?? []}
                  activeItemId={activeItem?.id}
                  onItemChange={onItemChange}
                  data-testid={suffixTestId('page-navigation-mobile', props)}
                />
              ))
              .otherwise(() => (
                <NumberedMenu
                  items={props.navigation ?? []}
                  activeItemId={activeItem?.id}
                  onItemChange={onItemChange}
                  data-testid={suffixTestId('page-navigation-desktop', props)}
                />
              ))}
          </Box>
        </Show>
        {context}
        <Show when={props.activityPanel}>{props.activityPanel}</Show>
        <Show when={props.isLocked}>
          <div
            css={css`
              cursor: not-allowed;
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              height: 100%;
              background-color: rgba(0, 0, 0, 0.1);
              z-index: 99999;
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            <Spinner />
          </div>
        </Show>
      </Stack>
    </VStack>
  );
}
